import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import type { Messaging } from 'firebase/messaging';
import { db, app } from '../config/firebase';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';

let messaging: Messaging | null = null;

// Initialize Firebase Cloud Messaging
export const initializeFCM = () => {
  try {
    if ('serviceWorker' in navigator) {
      messaging = getMessaging(app);
      return messaging;
    }
    console.warn('Service Worker not supported in this browser');
    return null;
  } catch (error) {
    console.error('Error initializing FCM:', error);
    return null;
  }
};

// Request notification permission and get FCM token
export const requestNotificationPermission = async (userId: string): Promise<string | null> => {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return null;
    }

    // Request permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('Notification permission granted');
      
      // Initialize FCM if not already done
      if (!messaging) {
        messaging = initializeFCM();
      }

      if (!messaging) {
        console.error('FCM not initialized');
        return null;
      }

      // Get FCM token
      // Get VAPID key from environment variables
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      
      if (!vapidKey) {
        console.error('VAPID key not found in environment variables');
        return null;
      }
      
      const token = await getToken(messaging, { vapidKey });
      
      if (token) {
        console.log('FCM Token:', token);
        
        // Save token to Firestore
        await saveFCMToken(userId, token);
        
        return token;
      } else {
        console.log('No registration token available');
        return null;
      }
    } else {
      console.log('Notification permission denied');
      return null;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return null;
  }
};

// Save FCM token to Firestore
const saveFCMToken = async (userId: string, token: string) => {
  try {
    const tokenRef = doc(db, 'fcmTokens', token);
    await setDoc(tokenRef, {
      userId,
      token,
      createdAt: new Date().toISOString(),
      platform: 'web',
      userAgent: navigator.userAgent
    });
    console.log('FCM token saved to Firestore');
  } catch (error) {
    console.error('Error saving FCM token:', error);
  }
};

// Remove FCM token from Firestore (on logout)
export const removeFCMToken = async (token: string) => {
  try {
    const tokenRef = doc(db, 'fcmTokens', token);
    await deleteDoc(tokenRef);
    console.log('FCM token removed from Firestore');
  } catch (error) {
    console.error('Error removing FCM token:', error);
  }
};

// Listen for foreground messages
export const onMessageListener = (): Promise<any> => {
  return new Promise((resolve) => {
    if (!messaging) {
      messaging = initializeFCM();
    }

    if (messaging) {
      onMessage(messaging, (payload) => {
        console.log('Message received in foreground:', payload);
        resolve(payload);
      });
    }
  });
};

// Show browser notification
export const showNotification = (title: string, options?: NotificationOptions) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, options);
  }
};

// Subscribe to order updates
export const subscribeToOrderUpdates = () => {
  onMessageListener()
    .then((payload) => {
      const { notification } = payload;
      if (notification) {
        showNotification(notification.title, {
          body: notification.body,
          icon: notification.icon || '/logo.png',
          badge: '/logo.png',
          tag: payload.data?.orderId || 'order-update',
          requireInteraction: true,
          data: payload.data
        });
      }
    })
    .catch((err) => console.error('Failed to receive message:', err));
};
